import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate, useParams, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { Header, Footer } from '@/components/layout'
import { HomePage } from '@/pages/HomePage'
import {
  BreastSurgeryPage,
  BodySurgeryPage,
  FaceSurgeryPage,
  BodyAestheticPage,
  FaceAestheticPage,
  RegenerativePage,
  LaserPage,
} from '@/pages/categories'

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()
  const location = useLocation()
  const mainRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, i18n])

  // Smooth scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-primary focus:text-ink focus:px-4 focus:py-2 focus:rounded-lg"
      >
        {i18n.t('aria.skipToContent')}
      </a>
      <Header />
      <div id="main-content" ref={mainRef} tabIndex={-1}>
        <Outlet />
      </div>
      <Footer />
    </>
  )
}

import { TreatmentPage } from '@/pages/TreatmentPage'
import { BlogPage } from '@/pages/BlogPage'
import { BookingPage } from '@/pages/BookingPage'
import { PrivacyCookiePage } from '@/pages/PrivacyCookiePage'
import { ContactsPage } from '@/pages/ContactsPage'

export function LanguageRouter() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<LanguageWrapper />}>
        <Route index element={<HomePage />} />
        <Route path="chirurgia-estetica-seno" element={<BreastSurgeryPage />} />
        <Route path="chirurgia-estetica-corpo" element={<BodySurgeryPage />} />
        <Route path="chirurgia-estetica-viso" element={<FaceSurgeryPage />} />
        <Route path="medicina-estetica-corpo" element={<BodyAestheticPage />} />
        <Route path="medicina-estetica-viso" element={<FaceAestheticPage />} />
        <Route path="medicina-rigenerativa" element={<RegenerativePage />} />
        <Route path="trattamenti-laser" element={<LaserPage />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="prenota-consulenza" element={<BookingPage />} />
        <Route path="contatti" element={<ContactsPage />} />
        <Route path="privacy" element={<PrivacyCookiePage />} />
        <Route path="cookie-policy" element={<PrivacyCookiePage />} />

        {/* English routes */}
        <Route path="breast-surgery" element={<BreastSurgeryPage />} />
        <Route path="body-surgery" element={<BodySurgeryPage />} />
        <Route path="face-surgery" element={<FaceSurgeryPage />} />
        <Route path="body-aesthetic-medicine" element={<BodyAestheticPage />} />
        <Route path="face-aesthetic-medicine" element={<FaceAestheticPage />} />
        <Route path="regenerative-medicine" element={<RegenerativePage />} />
        <Route path="laser-treatments" element={<LaserPage />} />
        <Route path="book-consultation" element={<BookingPage />} />
        <Route path="contacts" element={<ContactsPage />} />

        {/* Dynamic treatment route — must be last */}
        <Route path=":categorySlug/:treatmentSlug" element={<TreatmentPage />} />
      </Route>
    </Routes>
  )
}
